import { authedFetch, getToken } from '@/lib/api';
import { createContext, useContext, useEffect, useState } from 'react'

export type Density = 'comfortable' | 'compact' | 'glove'

interface DensityContextValue {
  density: Density
  setDensity: (d: Density) => void
}

const VALID_DENSITIES: Density[] = ['comfortable', 'compact', 'glove']

const DensityContext = createContext<DensityContextValue>({
  density: 'comfortable',
  setDensity: () => {},
})

function applyDensity(d: Density) {
  document.documentElement.setAttribute('data-density', d)
}

function isValidDensity(v: string | null): v is Density {
  return VALID_DENSITIES.includes(v as Density)
}

export function DensityProvider({ children }: { children: React.ReactNode }) {
  const [density, setDensityState] = useState<Density>(() => {
    const stored = localStorage.getItem('tenon-density-pref')
    return isValidDensity(stored) ? stored : 'comfortable'
  })

  useEffect(() => {
    const token = getToken()
    if (!token) return
    authedFetch('/api/v1/profile', {}, { redirectOnAuthFailure: false })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (isValidDensity(data?.density_preference)) {
          setDensityState(data.density_preference)
          localStorage.setItem('tenon-density-pref', data.density_preference)
          applyDensity(data.density_preference)
        }
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    applyDensity(density)
    localStorage.setItem('tenon-density-pref', density)
  }, [density])

  const setDensity = (d: Density) => {
    setDensityState(d)
    const token = getToken()
    if (token) {
      authedFetch('/api/v1/profile/density', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ density: d }),
      }, { redirectOnAuthFailure: false }).catch(() => {})
    }
  }

  return (
    <DensityContext.Provider value={{ density, setDensity }}>
      {children}
    </DensityContext.Provider>
  )
}

export const useDensity = () => useContext(DensityContext)
